import { useState, useEffect, useCallback, useRef } from 'react';
import { inAppNotificationService } from '../services';

interface UseNotificationPollingOptions {
  enabled?: boolean;
  interval?: number; // in milliseconds
  slowInterval?: number; // interval when slowing down
}

/**
 * Custom hook for polling notification unread count
 * Features:
 * - Adaptive polling: slows down when tab is hidden or no new notifications
 * - Default: 2 minutes active, 5 minutes when hidden
 * - Smart polling: increases to 5 minutes after 10 minutes of no changes
 */
export const useNotificationPolling = (
  options: UseNotificationPollingOptions = {}
) => {
  const { enabled = true, interval = 120000, slowInterval = 300000 } = options; // Default 2min / 5min
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentInterval, setCurrentInterval] = useState<number>(interval);
  
  const lastChangeTime = useRef<number>(Date.now());
  const previousCount = useRef<number>(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);
    try {
      const count = await inAppNotificationService.getUnreadCount();
      
      // Check if count changed
      if (count !== previousCount.current) {
        lastChangeTime.current = Date.now();
        previousCount.current = count;
      }
      
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Initial fetch
  useEffect(() => {
    if (enabled) {
      fetchUnreadCount();
    }
  }, [enabled, fetchUnreadCount]);

  // Adaptive interval based on activity and visibility
  useEffect(() => {
    const updateInterval = () => {
      const timeSinceChange = Date.now() - lastChangeTime.current;
      const isHidden = document.hidden;
      
      // If tab hidden, use slow interval
      if (isHidden) {
        setCurrentInterval(slowInterval);
      }
      // If no changes for 10 minutes, slow down
      else if (timeSinceChange > 10 * 60 * 1000) {
        setCurrentInterval(slowInterval);
      }
      // Otherwise use normal interval
      else {
        setCurrentInterval(interval);
      }
    };

    const handleVisibilityChange = () => {
      updateInterval();
      // Fetch immediately when tab becomes visible
      if (!document.hidden) {
        fetchUnreadCount();
      }
    };

    // Update interval every minute
    const intervalChecker = setInterval(updateInterval, 60000);
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalChecker);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [interval, slowInterval, fetchUnreadCount]);

  // Polling interval
  useEffect(() => {
    if (!enabled) return;

    const pollInterval = setInterval(() => {
      fetchUnreadCount();
    }, currentInterval);

    return () => {
      clearInterval(pollInterval);
    };
  }, [enabled, currentInterval, fetchUnreadCount]);

  return {
    unreadCount,
    setUnreadCount, // Allow manual updates (e.g., after marking as read)
    loading,
    error,
    refresh: fetchUnreadCount,
    currentInterval, // Expose for debugging
  };
};
